import {SourcePMEIIdsByTemplatePMDVIdREST} from 'procedures/network/ProcedureRunDAO';
import {Condition} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {ExperimentProcedure} from 'studies/typed/shared/ExperimentalInputs';
import {UnitOpLinkEdge} from 'studies/typed/shared/unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';

/**
 * Helper function that generates a unique PMEI ID based on the method name, condition name, and replicate number.
 * Method names are unique within a procedure, and conditions are unique within a method, so this ID is guaranteed to be unique.
 */
export function getDestinationPMEIId(
  methodName: string,
  conditionName: string,
  replicateNumber: number
): string {
  return `${methodName}-${conditionName}-${replicateNumber}`;
}

export function getDestinationPMEIIds(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Set<string> {
  if (procedure == null || conditions == null) {
    return new Set();
  }
  return new Set(
    procedure.methods.flatMap((method) =>
      conditions.flatMap((condition) =>
        [...Array(condition.numReplicates).keys()].map((i) =>
          getDestinationPMEIId(method.name, condition.name, i + 1)
        )
      )
    )
  );
}

export function getDestinationPMEIs(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Array<UnitOperationFields> {
  if (procedure == null || conditions == null) {
    return [];
  }
  return procedure.methods.flatMap((method, methodIndex) =>
    conditions.flatMap((condition, conditionIndex) =>
      [...Array(condition.numReplicates).keys()].map((replicateIndex) => ({
        pmeiId: getDestinationPMEIId(method.name, condition.name, replicateIndex + 1),
        procedureRunName: null,
        procedureRunId: procedure.id,
        procedureRunDisplayId: null, // Doesn't need a display ID assigned since it's not used for the destination procedure run
        procedureRunOrderKey: 0, // Only one run is being added
        methodName: method.name,
        templateMethodId: method.id,
        methodOrderKey: methodIndex,
        conditionName: condition.name,
        conditionOrderKey: conditionIndex,
        replicateNumber: replicateIndex + 1,
        procedureType: procedure.procedureType,
        status: null,
      }))
    )
  );
}

function getDestinationPMEIToTemplatePMDVIdMapping(
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null
): Record<string, string> {
  const destinationPMEIsToTemplatePMDVs: Record<string, string> = {};
  if (procedure != null && destinationPMEIs != null) {
    procedure.methods.forEach((method) => {
      destinationPMEIs.forEach((destinationPMEI) => {
        if (method.name === destinationPMEI.methodName) {
          const templatePMDVId = method.id;
          if (templatePMDVId != null) {
            destinationPMEIsToTemplatePMDVs[destinationPMEI.pmeiId] = templatePMDVId;
          }
        }
      });
    });
  }
  return destinationPMEIsToTemplatePMDVs;
}

function getDestinationPMEIToConditionReplicateMapping(
  destinationPMEIs: Array<UnitOperationFields> | null,
  conditions: Array<Condition> | null
): Record<string, {conditionIndex: number; replicateIndex: number}> {
  const destinationPMEIIdsToConditionReplicateMapping: Record<
    string,
    {conditionIndex: number; replicateIndex: number}
  > = {};
  if (destinationPMEIs != null && conditions != null) {
    destinationPMEIs.forEach((destinationPMEI) => {
      const conditionIndex = conditions.findIndex(
        (condition) => condition.name === destinationPMEI.conditionName
      );
      const replicateIndex = destinationPMEI.replicateNumber - 1;
      destinationPMEIIdsToConditionReplicateMapping[destinationPMEI.pmeiId] = {
        conditionIndex,
        replicateIndex,
      };
    });
  }
  return destinationPMEIIdsToConditionReplicateMapping;
}

/* Create a mapping of source PMEIs to template PMDVs for each condition and replicate.
 */
export function createSourcePMEIsToTemplatePMDVsMapping(
  newLinks: Array<UnitOpLinkEdge> | null,
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Array<Array<SourcePMEIIdsByTemplatePMDVIdREST>> {
  if (newLinks == null || destinationPMEIs == null || procedure == null || conditions == null) {
    return [];
  }
  const sourcePMEIIdsByTemplatePMDVId: Array<Array<Record<string, Array<string>>>> = [];
  for (let i = 0; i < conditions.length; i++) {
    const numReplicates = conditions[i].numReplicates;
    const replicateArray: Array<Record<string, Array<string>>> = [];
    for (let j = 0; j < numReplicates; j++) {
      replicateArray[j] = {};
    }
    sourcePMEIIdsByTemplatePMDVId[i] = replicateArray;
  }

  const destinationPMEIsToTemplatePMDVIds: Record<string, string> = getDestinationPMEIToTemplatePMDVIdMapping(
    destinationPMEIs,
    procedure
  );
  const destinationPMEIIdsToConditionReplicateIndices = getDestinationPMEIToConditionReplicateMapping(
    destinationPMEIs,
    conditions
  );
  if (procedure != null) {
    newLinks?.forEach((edge) => {
      const sourcePMEIId = edge.sourcePMEIId;
      const destinationPMEIId = edge.destinationPMEIId;
      const templatePMDVId = destinationPMEIsToTemplatePMDVIds[destinationPMEIId];
      const conditionReplicateIndices = destinationPMEIIdsToConditionReplicateIndices[destinationPMEIId];
      if (conditionReplicateIndices == null) {
        return;
      }
      const {conditionIndex, replicateIndex} = conditionReplicateIndices;
      if (templatePMDVId != null) {
        if (!sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId]) {
          sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId] = [];
        }
        sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId].push(sourcePMEIId);
      }
    });
  }
  return sourcePMEIIdsByTemplatePMDVId;
}
